import { clients, HttpError } from '@/lib/https';
import { useAuthStore } from '@/state/store/auth';
import { clearAuthStorage } from '@/state/store/auth/auth-storage';
import { AccountActivationPayload, ActivationCodeExpirationResponse } from '../types/auth.type';
export type { ActivationCodeExpirationResponse };

/**
 * Authentication API Utilities
 *
 * A set of low-level API functions for authentication-related operations. Sign-in is IAM's hosted
 * (OIDC) login (see `oidc.service.ts`); there is no password anywhere.
 *
 * Functions:
 * - `signout`: Logs out the user and removes relevant local storage
 * - `getRefreshToken`: Refreshes the user's access token using a stored refresh token
 * - `accountActivation`: Activates an invited account from the code in its activation link
 * - `resendActivation`: Resends the account activation link
 *
 * Interfaces & Types:
 * - `MFASigninResponse`: Tokens IAM returns (organization switch)
 * - `AccountActivationPayload`: Data used for account activation request
 */

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

const getApiUrl = (path: string) => {
  const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${cleanPath}`;
};

export const signout = async (): Promise<{ isSuccess: true }> => {
  try {
    clearAuthStorage();
    const url = '/iam/v4/auth/logout';
    return await clients.post(
      url,
      JSON.stringify({
        // A cookie session has no refresh token in the store; IAM ends it from the cookie alone.
        ...(useAuthStore.getState().refreshToken && {
          refreshToken: useAuthStore.getState().refreshToken,
        }),
      })
    );
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

/** Whether the browser holds a valid IAM session cookie (a cookie-only login has no token). */
export const hasSessionCookie = async (): Promise<boolean> => {
  try {
    const response = await fetch(getApiUrl('/iam/v4/auth/me'), {
      headers: { 'x-blocks-key': projectKey },
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
};

/** Refreshes a session signed in through the Blocks OIDC client; IAM requires its `client_id`. */
const getOidcRefreshToken = async () => {
  const response = await fetch(getApiUrl('/iam/v4/oidc/token'), {
    method: 'POST',
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: useAuthStore.getState().refreshToken ?? '',
      client_id: import.meta.env.VITE_BLOCKS_OIDC_CLIENT_ID ?? '',
    }),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-blocks-key': projectKey,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

export const getRefreshToken = async () => {
  if (useAuthStore.getState().isOidcSession) return getOidcRefreshToken();

  // A session from before sign-in moved to OIDC (direct social login) refreshes at `auth/refresh`
  // until it ends; every new sign-in is an OIDC session.

  const response = await fetch(getApiUrl('/iam/v4/auth/refresh'), {
    method: 'POST',
    body: JSON.stringify({
      refresh_token: useAuthStore.getState().refreshToken ?? '',
    }),
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

export const validateActivationCode = async (payload: {
  activationCode: string;
  projectKey: string;
}): Promise<ActivationCodeExpirationResponse> => {
  const url = '/iam/v4/auth/validate-activation';
  return clients.post(url, JSON.stringify({ activationCode: payload.activationCode }));
};

/** Activates the invited account; no password is set, since the person signs in with SSO. */
export const accountActivation = async (data: AccountActivationPayload) => {
  const payload = {
    code: data.code,
    preventPostEvent: true,
  };
  const url = '/iam/v4/auth/activate';
  return clients.post(url, JSON.stringify(payload));
};

export const resendActivation = async (data: { userId: string; projectKey?: string }) => {
  const url = '/iam/v4/auth/resend-activation';
  return clients.post(url, JSON.stringify({ userId: data.userId }));
};

export const switchOrganization = async (orgId: string): Promise<MFASigninResponse> => {
  return clients.post<MFASigninResponse>(
    '/iam/v4/auth/switch-org',
    JSON.stringify({ organization_id: orgId })
  );
};
